"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Section, Typography } from "@/components/ui";
import { env } from "@/lib/environment";
import { NewButton } from "@/components/ui/NewButton";

type BenefitItem = string | { label: string; description?: string };

interface OnPremiseVideoFrameContent {
  title: string;
  description?: string;
  benefits?: BenefitItem[];
  ctaText?: string;
  ctaLink?: string;
  frameImage: string;
  video: {
    src: string;
  };
}

interface OnPremiseVideoFrameSectionProps {
  content: OnPremiseVideoFrameContent;
}

export default function OnPremiseVideoFrameSection({ content }: OnPremiseVideoFrameSectionProps) {
  return (
    <Section variant="default" background="transparent" className="bg-secondary/5">
      <div className="mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 pr-0 md:pr-4">
            <Typography variant="h2" as="h2" className="text-text-primary">
              {content.title}
            </Typography>
            <Typography variant="body" as="p" className="text-text-secondary">
              {content.description}
            </Typography>

            {/* Benefits with Checkmarks */}
            {content.benefits && content.benefits.length > 0 && (
              <div className="space-y-4">
                {content.benefits.map((benefit, index) => {
                  const isObject = typeof benefit === "object";
                  const label = isObject ? (benefit as { label: string; description?: string }).label : "";
                  const description = isObject ? (benefit as { label: string; description?: string }).description : undefined;
                  const text = typeof benefit === "string" ? benefit : "";
                  const strongMatch = text.match(/^Strong\s+/);
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div className="shrink-0 mt-1">
                        <Image
                          src={`${env.NEXT_PUBLIC_CDN_URL}/images/industries/corporate/tick.DgpDqOZ0stAU.svg`}
                          alt="check mark"
                          title="check mark"
                          width={20}
                          height={20}
                          loading="lazy"
                        />
                      </div>
                      <Typography variant="body" as="p" className="text-text-primary">
                        {isObject ? (
                          <>
                            <span className="font-semibold">{label}</span>
                            {description != null && description !== "" && (
                              <span className="font-normal text-text-secondary"> - {description}</span>
                            )}
                          </>
                        ) : strongMatch ? (
                          <>
                            <strong>{strongMatch[0].trim()}</strong> {text.slice(strongMatch[0].length)}
                          </>
                        ) : (
                          text
                        )}
                      </Typography>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Optional CTA */}
            {content.ctaText && content.ctaLink && (
              <Link href={content.ctaLink}>
                <NewButton variant="linkPrimary" asChild>
                  {content.ctaText}
                </NewButton>
              </Link>
            )}
          </div>

          {/* Right: TV Frame with Video inside */}
          <div className="relative w-full max-w-[600px] mx-auto lg:mx-0">
            <div className="relative aspect-[4/3] w-full">
              {/* TV Frame image */}
              <Image
                src={content.frameImage}
                alt="On-premise digital signage display"
                title="on-premise digital signage display"
                fill
                className="object-contain"
                sizes="(max-width: 1024px) 100vw, 600px"
              />
              {/* Video inside the frame - positioned in screen area, minimal inset to avoid white space */}
              <div className="absolute top-[calc(2%-3px)] right-[2%] bottom-[2%] left-[2%] md:top-[calc(1%-3px)] md:right-[1%] md:bottom-[2%] md:left-[1%] rounded-lg overflow-hidden">
                <video
                  src={content.video.src}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full"
                  aria-label="On-premise digital signage demo"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
